import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { trpc } from '../lib/trpc';
import { useProject } from '../context';

/**
 * Bots page component.
 *
 * Displays bot configurations with management capabilities.
 * Styled to match the Prompt Lab design.
 */
// Bot status type from the API
type BotStatus = 'active' | 'inactive' | 'archived';

// Category definition for filtering
interface Category {
  id: string;
  label: string;
  count: number;
}

// Bot type with extended fields for display
interface BotData {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  icon: string | null;
  color: string | null;
  status: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

// Helper to generate mock performance stats based on bot data
function getMockStats(bot: BotData) {
  // Generate consistent stats based on bot id hash
  const hash = bot.id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const successRate = 90 + (hash % 10); // 90-99%
  const avgTime = (0.1 + (hash % 20) / 10).toFixed(2); // 0.1-2.0s
  const lastRunMinutes = hash % 60;
  const lastRun = lastRunMinutes < 5 ? 'Just now' : `${lastRunMinutes} min ago`;

  return { successRate, avgTime: `${avgTime}s`, lastRun };
}

// Helper to determine bot type based on data
function getBotType(bot: BotData): 'hardcoded' | 'prompt-based' {
  // Use slug pattern to determine type (mock logic)
  return bot.slug?.includes('-api') || bot.slug?.includes('-core') ? 'hardcoded' : 'prompt-based';
}

// Helper to determine visibility (mock logic based on status)
function getBotVisibility(bot: BotData): 'public' | 'private' {
  return bot.status === 'active' ? 'public' : 'private';
}

export function BotsPage() {
  const navigate = useNavigate();
  const { selectBot, refreshBots } = useProject();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<BotStatus | ''>('');
  const [selectedBot, setSelectedBot] = useState<BotData | null>(null);
  const [categoryFilter, setCategoryFilter] = useState('all');

  const botsQuery = trpc.bots.list.useQuery({
    search: search || undefined,
    status: statusFilter || undefined,
  });

  const duplicateMutation = trpc.bots.duplicate.useMutation({
    onSuccess: (newBot) => {
      // Refresh the bots list to show the newly duplicated bot
      botsQuery.refetch();
      refreshBots();
      // Select the newly created bot
      setSelectedBot({
        id: newBot.id,
        slug: newBot.slug,
        name: newBot.name,
        description: newBot.description,
        icon: newBot.icon,
        color: newBot.color,
        status: newBot.status,
        createdAt: newBot.createdAt,
        updatedAt: newBot.updatedAt,
      });
    },
  });

  const handleDuplicate = () => {
    if (selectedBot) {
      duplicateMutation.mutate({ id: selectedBot.id });
    }
  };

  // Generate categories from bot data
  const categories: Category[] = [
    { id: 'all', label: 'All Bots', count: botsQuery.data?.bots.length ?? 0 },
    { id: 'active', label: 'Active', count: botsQuery.data?.bots.filter(b => b.status === 'active').length ?? 0 },
    { id: 'inactive', label: 'Inactive', count: botsQuery.data?.bots.filter(b => b.status === 'inactive').length ?? 0 },
    { id: 'archived', label: 'Archived', count: botsQuery.data?.bots.filter(b => b.status === 'archived').length ?? 0 },
  ];

  // Filter bots by category
  const filteredBots = categoryFilter === 'all'
    ? botsQuery.data?.bots
    : botsQuery.data?.bots.filter(b => b.status === categoryFilter);

  const handleBotClick = (bot: BotData) => {
    setSelectedBot(bot);
  };

  const handleBotNavigate = (botId: string) => {
    selectBot(botId);
    navigate('/dashboard');
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Bots</h1>
          <p style={styles.subtitle}>AI assistants and bot configurations</p>
        </div>
        <button style={styles.createButton} onClick={() => navigate('/bots/new')}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M7 1V13M1 7H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          Create Bot
        </button>
      </div>

      {/* Category Filters */}
      <div style={styles.categoryFilters}>
        {categories.map(cat => (
          <button
            key={cat.id}
            onClick={() => {
              setCategoryFilter(cat.id);
              if (cat.id === 'all') {
                setStatusFilter('');
              } else {
                setStatusFilter(cat.id as BotStatus);
              }
            }}
            style={{
              ...styles.categoryButton,
              ...(categoryFilter === cat.id ? styles.categoryButtonActive : styles.categoryButtonInactive),
            }}
          >
            {cat.label}
            <span style={{
              ...styles.categoryCount,
              background: categoryFilter === cat.id ? 'rgba(255,255,255,0.2)' : 'rgba(93, 86, 78, 0.08)',
            }}>
              {cat.count}
            </span>
          </button>
        ))}
      </div>

      {/* Search Input */}
      <div style={styles.searchContainer}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={styles.searchIcon}>
          <circle cx="7" cy="7" r="5.5" stroke="#9B9285" strokeWidth="1.5"/>
          <path d="M11 11L14 14" stroke="#9B9285" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
        <input
          type="text"
          placeholder="Search bots..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={styles.searchInput}
        />
      </div>

      {/* Content */}
      {botsQuery.isLoading ? (
        <div style={styles.loading}>Loading bots...</div>
      ) : botsQuery.error ? (
        <div style={styles.error}>
          Error loading bots: {botsQuery.error.message}
        </div>
      ) : filteredBots?.length === 0 ? (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>🤖</div>
          <h3 style={styles.emptyTitle}>No bots found</h3>
          <p style={styles.emptyText}>
            {categoryFilter !== 'all'
              ? `No ${categoryFilter} bots available.`
              : 'Create your first bot to start engaging with users.'
            }
          </p>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: selectedBot ? '1fr 380px' : '1fr',
          gap: '20px',
        }}>
          {/* Bots Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: selectedBot ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
            gap: '16px',
            alignContent: 'start',
          }}>
            {filteredBots?.map((bot) => {
              const stats = getMockStats(bot);
              const botType = getBotType(bot);
              const visibility = getBotVisibility(bot);
              const isSelected = selectedBot?.id === bot.id;

              return (
                <div
                  key={bot.id}
                  onClick={() => handleBotClick(bot)}
                  style={{
                    ...styles.botCard,
                    border: isSelected ? '2px solid #5A8A5E' : '1px solid rgba(93, 86, 78, 0.06)',
                  }}
                >
                  {/* Card Header */}
                  <div style={styles.cardHeader}>
                    <div style={styles.cardHeaderLeft}>
                      <span style={styles.botIcon}>{bot.icon || '🤖'}</span>
                      <div>
                        <h3 style={styles.botName}>{bot.name}</h3>
                        <div style={styles.badgeContainer}>
                          <span style={{
                            ...styles.typeBadge,
                            background: botType === 'hardcoded' ? 'rgba(90, 138, 94, 0.1)' : 'rgba(90, 106, 138, 0.1)',
                            color: botType === 'hardcoded' ? '#5A8A5E' : '#5A6A8A',
                          }}>
                            {botType === 'hardcoded' ? 'Hardcoded' : 'Prompt'}
                          </span>
                          {visibility === 'private' && (
                            <span style={styles.labBadge}>
                              Lab
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    {/* Status Toggle */}
                    <div
                      style={{
                        ...styles.toggle,
                        background: bot.status === 'active' ? '#5A8A5E' : 'rgba(93, 86, 78, 0.15)',
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div style={{
                        ...styles.toggleKnob,
                        left: bot.status === 'active' ? '16px' : '2px',
                      }} />
                    </div>
                  </div>

                  {/* Description */}
                  <p style={styles.botDescription}>
                    {bot.description
                      ? (bot.description.length > 80 ? `${bot.description.slice(0, 80)}...` : bot.description)
                      : 'No description provided'
                    }
                  </p>

                  {/* Slug Badge */}
                  <div style={styles.slugBadge}>
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <path d="M3 7L7 3M3.5 3H3V3.5M7 6.5V7H6.5" stroke="#5A6A8A" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span style={styles.slugText}>{bot.slug}</span>
                  </div>

                  {/* Card Footer */}
                  <div style={styles.cardFooter}>
                    <span style={styles.lastRun}>{stats.lastRun}</span>
                    <div style={styles.successIndicator}>
                      <svg width="6" height="6" viewBox="0 0 6 6">
                        <circle
                          cx="3"
                          cy="3"
                          r="3"
                          fill={stats.successRate >= 95 ? '#5A8A5E' : stats.successRate >= 90 ? '#9B7B3C' : '#8B5A50'}
                        />
                      </svg>
                      <span style={styles.successRate}>{stats.successRate}%</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Detail Panel */}
          {selectedBot && (
            <BotDetailPanel
              bot={selectedBot}
              onClose={() => setSelectedBot(null)}
              onNavigate={() => handleBotNavigate(selectedBot.id)}
              onEdit={() => navigate(`/bots/${selectedBot.id}/edit`)}
              onDuplicate={handleDuplicate}
              isDuplicating={duplicateMutation.isPending}
            />
          )}
        </div>
      )}
    </div>
  );
}

// Bot Detail Panel Component
interface BotDetailPanelProps {
  bot: BotData;
  onClose: () => void;
  onNavigate: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  isDuplicating?: boolean;
}

function BotDetailPanel({ bot, onClose, onNavigate, onEdit, onDuplicate, isDuplicating }: BotDetailPanelProps) {
  const stats = getMockStats(bot);
  const botType = getBotType(bot);
  const visibility = getBotVisibility(bot);

  return (
    <div style={styles.detailPanel}>
      {/* Panel Header */}
      <div style={styles.panelHeader}>
        <div style={styles.panelHeaderLeft}>
          <span style={styles.panelIcon}>{bot.icon || '🤖'}</span>
          <div>
            <h2 style={styles.panelTitle}>{bot.name}</h2>
            <div style={styles.panelBadges}>
              <span style={{
                ...styles.panelTypeBadge,
                background: botType === 'hardcoded' ? 'rgba(90, 138, 94, 0.1)' : 'rgba(90, 106, 138, 0.1)',
                color: botType === 'hardcoded' ? '#5A8A5E' : '#5A6A8A',
              }}>
                {botType === 'hardcoded' ? 'Hardcoded' : 'Prompt-based'}
              </span>
            </div>
          </div>
        </div>
        <button onClick={onClose} style={styles.closeButton}>×</button>
      </div>

      {/* Visibility Toggle */}
      <div style={styles.visibilitySection}>
        <div>
          <p style={styles.visibilityLabel}>Visibility</p>
          <p style={styles.visibilityDescription}>
            {visibility === 'public'
              ? 'Bot is live and available to users'
              : 'Bot is in private testing mode'}
          </p>
        </div>
        <div style={styles.visibilityButtons}>
          <button
            style={{
              ...styles.visibilityButton,
              ...(visibility === 'private' ? styles.visibilityButtonActive : {}),
              borderColor: visibility === 'private' ? '#9B7B3C' : 'rgba(93, 86, 78, 0.15)',
              background: visibility === 'private' ? 'rgba(155, 123, 60, 0.1)' : 'white',
              color: visibility === 'private' ? '#9B7B3C' : '#9B9285',
            }}
          >
            🔒 Private
          </button>
          <button
            style={{
              ...styles.visibilityButton,
              ...(visibility === 'public' ? styles.visibilityButtonActive : {}),
              borderColor: visibility === 'public' ? '#5A8A5E' : 'rgba(93, 86, 78, 0.15)',
              background: visibility === 'public' ? 'rgba(90, 138, 94, 0.1)' : 'white',
              color: visibility === 'public' ? '#5A8A5E' : '#9B9285',
            }}
          >
            🌐 Public
          </button>
        </div>
      </div>

      {/* Description */}
      <p style={styles.panelDescription}>
        {bot.description || 'No description provided for this bot.'}
      </p>

      {/* Slug Badge */}
      <div style={styles.panelSlugBadge}>
        <div style={styles.panelSlugIcon}>
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path d="M5 13L13 5M6 5H5V6M13 12V13H12" stroke="#5A6A8A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <div>
          <p style={styles.panelSlugLabel}>Bot Slug</p>
          <p style={styles.panelSlugValue}>{bot.slug}</p>
        </div>
      </div>

      {/* Stats */}
      <div style={styles.statsGrid}>
        <div style={styles.statItem}>
          <p style={styles.statLabel}>Last Run</p>
          <p style={styles.statValue}>{stats.lastRun}</p>
        </div>
        <div style={styles.statItem}>
          <p style={styles.statLabel}>Avg Time</p>
          <p style={styles.statValue}>{stats.avgTime}</p>
        </div>
        <div style={styles.statItem}>
          <p style={styles.statLabel}>Success</p>
          <p style={{
            ...styles.statValue,
            color: stats.successRate >= 95 ? '#5A8A5E' : '#9B7B3C',
          }}>{stats.successRate}%</p>
        </div>
      </div>

      {/* Status Section */}
      <div style={styles.statusSection}>
        <div style={styles.statusHeader}>
          <p style={styles.statusLabel}>Status</p>
          <span style={{
            ...styles.statusBadge,
            ...getStatusBadgeStyle(bot.status ?? ''),
          }}>
            {bot.status ?? 'Unknown'}
          </span>
        </div>
      </div>

      {/* Metadata */}
      <div style={styles.metadataSection}>
        <p style={styles.metadataLabel}>Metadata</p>
        <div style={styles.metadataGrid}>
          <div style={styles.metadataItem}>
            <span style={styles.metadataKey}>Created</span>
            <span style={styles.metadataValue}>
              {bot.createdAt ? new Date(bot.createdAt).toLocaleDateString() : 'Unknown'}
            </span>
          </div>
          <div style={styles.metadataItem}>
            <span style={styles.metadataKey}>Updated</span>
            <span style={styles.metadataValue}>
              {bot.updatedAt ? new Date(bot.updatedAt).toLocaleDateString() : 'Unknown'}
            </span>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div style={styles.actionButtons}>
        <button onClick={onNavigate} style={styles.primaryButton}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M7 1V13M1 7H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          Open Dashboard
        </button>
        <button onClick={onEdit} style={styles.secondaryButton}>
          Edit Bot
        </button>
      </div>
      <div style={{ marginTop: '8px' }}>
        <button
          onClick={onDuplicate}
          disabled={isDuplicating}
          style={{
            ...styles.secondaryButton,
            width: '100%',
            opacity: isDuplicating ? 0.6 : 1,
            cursor: isDuplicating ? 'not-allowed' : 'pointer',
          }}
        >
          {isDuplicating ? 'Duplicating...' : 'Duplicate'}
        </button>
      </div>
    </div>
  );
}

function getStatusBadgeStyle(status: string): React.CSSProperties {
  switch (status) {
    case 'active':
      return { backgroundColor: 'rgba(90, 138, 94, 0.15)', color: '#5A8A5E' };
    case 'inactive':
      return { backgroundColor: 'rgba(139, 90, 80, 0.15)', color: '#8B5A50' };
    case 'archived':
      return { backgroundColor: 'rgba(93, 86, 78, 0.15)', color: '#5C564E' };
    default:
      return { backgroundColor: 'rgba(93, 86, 78, 0.15)', color: '#5C564E' };
  }
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    maxWidth: '1400px',
    padding: '32px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  title: {
    fontFamily: '"Sentient", Georgia, serif',
    fontSize: '24px',
    fontWeight: 600,
    color: '#3D3A36',
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '14px',
    color: '#9B9285',
  },
  createButton: {
    padding: '12px 20px',
    borderRadius: '10px',
    background: '#3D3A36',
    border: 'none',
    color: '#FAF9F7',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  categoryFilters: {
    display: 'flex',
    gap: '8px',
    marginBottom: '20px',
    flexWrap: 'wrap',
  },
  categoryButton: {
    padding: '8px 16px',
    borderRadius: '100px',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'inherit',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  categoryButtonActive: {
    border: 'none',
    background: '#3D3A36',
    color: '#FAF9F7',
  },
  categoryButtonInactive: {
    border: '1px solid rgba(93, 86, 78, 0.15)',
    background: 'white',
    color: '#5C564E',
  },
  categoryCount: {
    padding: '2px 6px',
    borderRadius: '100px',
    fontSize: '11px',
  },
  searchContainer: {
    position: 'relative',
    marginBottom: '24px',
    maxWidth: '400px',
  },
  searchIcon: {
    position: 'absolute',
    left: '14px',
    top: '50%',
    transform: 'translateY(-50%)',
  },
  searchInput: {
    width: '100%',
    padding: '12px 16px 12px 44px',
    border: '1px solid rgba(93, 86, 78, 0.15)',
    borderRadius: '10px',
    fontSize: '14px',
    fontFamily: 'inherit',
    outline: 'none',
    background: 'white',
  },
  loading: {
    padding: '60px 20px',
    textAlign: 'center',
    color: '#9B9285',
    fontSize: '14px',
  },
  error: {
    padding: '16px 20px',
    backgroundColor: 'rgba(139, 90, 80, 0.1)',
    color: '#8B5A50',
    borderRadius: '10px',
    fontSize: '14px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '80px 20px',
    backgroundColor: 'white',
    borderRadius: '16px',
    border: '1px solid rgba(93, 86, 78, 0.06)',
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: '16px',
  },
  emptyTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#3D3A36',
    marginBottom: '8px',
  },
  emptyText: {
    fontSize: '14px',
    color: '#9B9285',
  },
  botCard: {
    background: 'white',
    borderRadius: '14px',
    padding: '20px',
    cursor: 'pointer',
    transition: 'border-color 0.15s ease',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: '12px',
  },
  cardHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  botIcon: {
    fontSize: '20px',
  },
  botName: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#3D3A36',
    margin: 0,
  },
  badgeContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    marginTop: '4px',
  },
  typeBadge: {
    padding: '2px 6px',
    borderRadius: '4px',
    fontSize: '9px',
    fontWeight: 500,
  },
  labBadge: {
    padding: '2px 6px',
    borderRadius: '4px',
    background: 'rgba(155, 123, 60, 0.1)',
    fontSize: '9px',
    fontWeight: 500,
    color: '#9B7B3C',
  },
  toggle: {
    width: '32px',
    height: '18px',
    borderRadius: '9px',
    position: 'relative',
  },
  toggleKnob: {
    width: '14px',
    height: '14px',
    borderRadius: '50%',
    background: 'white',
    position: 'absolute',
    top: '2px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
    transition: 'left 0.15s ease',
  },
  botDescription: {
    fontSize: '11px',
    color: '#706A62',
    marginBottom: '12px',
    lineHeight: '1.5',
  },
  slugBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 8px',
    borderRadius: '4px',
    background: 'rgba(90, 106, 138, 0.08)',
    marginBottom: '10px',
    width: 'fit-content',
  },
  slugText: {
    fontSize: '9px',
    color: '#5A6A8A',
    fontWeight: 500,
  },
  cardFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  lastRun: {
    fontSize: '10px',
    color: '#9B9285',
  },
  successIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  successRate: {
    fontSize: '10px',
    color: '#9B9285',
  },
  // Detail Panel Styles
  detailPanel: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    border: '1px solid rgba(93, 86, 78, 0.06)',
    position: 'sticky',
    top: '24px',
    alignSelf: 'start',
    maxHeight: 'calc(100vh - 120px)',
    overflowY: 'auto',
  },
  panelHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '16px',
  },
  panelHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  panelIcon: {
    fontSize: '28px',
  },
  panelTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#3D3A36',
    margin: 0,
  },
  panelBadges: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginTop: '4px',
  },
  panelTypeBadge: {
    padding: '3px 8px',
    borderRadius: '4px',
    fontSize: '10px',
    fontWeight: 500,
  },
  closeButton: {
    width: '28px',
    height: '28px',
    borderRadius: '8px',
    border: 'none',
    background: 'rgba(93, 86, 78, 0.06)',
    cursor: 'pointer',
    color: '#706A62',
    fontSize: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  visibilitySection: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 14px',
    borderRadius: '10px',
    background: 'rgba(93, 86, 78, 0.03)',
    marginBottom: '16px',
  },
  visibilityLabel: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#3D3A36',
    marginBottom: '2px',
  },
  visibilityDescription: {
    fontSize: '11px',
    color: '#9B9285',
  },
  visibilityButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  visibilityButton: {
    padding: '6px 12px',
    borderRadius: '6px',
    border: '1px solid rgba(93, 86, 78, 0.15)',
    fontSize: '11px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    background: 'white',
  },
  visibilityButtonActive: {
    borderWidth: '2px',
  },
  panelDescription: {
    fontSize: '12px',
    color: '#5C564E',
    marginBottom: '16px',
    lineHeight: '1.6',
  },
  panelSlugBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px',
    borderRadius: '10px',
    background: 'linear-gradient(135deg, rgba(90, 106, 138, 0.08) 0%, rgba(90, 138, 94, 0.08) 100%)',
    marginBottom: '16px',
    border: '1px solid rgba(90, 106, 138, 0.15)',
  },
  panelSlugIcon: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    background: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
  panelSlugLabel: {
    fontSize: '10px',
    color: '#9B9285',
    textTransform: 'uppercase',
    marginBottom: '2px',
  },
  panelSlugValue: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#5A6A8A',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '10px',
    marginBottom: '16px',
  },
  statItem: {
    padding: '10px',
    background: 'rgba(93, 86, 78, 0.03)',
    borderRadius: '8px',
  },
  statLabel: {
    fontSize: '9px',
    color: '#9B9285',
    textTransform: 'uppercase',
    marginBottom: '2px',
  },
  statValue: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#3D3A36',
  },
  statusSection: {
    padding: '12px',
    background: 'rgba(93, 86, 78, 0.03)',
    borderRadius: '10px',
    marginBottom: '16px',
  },
  statusHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  statusLabel: {
    fontSize: '10px',
    color: '#9B9285',
    textTransform: 'uppercase',
    fontWeight: 600,
  },
  statusBadge: {
    padding: '4px 10px',
    borderRadius: '100px',
    fontSize: '11px',
    fontWeight: 500,
    textTransform: 'capitalize',
  },
  metadataSection: {
    marginBottom: '16px',
  },
  metadataLabel: {
    fontSize: '10px',
    color: '#9B9285',
    textTransform: 'uppercase',
    fontWeight: 600,
    marginBottom: '10px',
  },
  metadataGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  metadataItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 10px',
    background: 'rgba(93, 86, 78, 0.03)',
    borderRadius: '6px',
  },
  metadataKey: {
    fontSize: '11px',
    color: '#706A62',
  },
  metadataValue: {
    fontSize: '11px',
    fontWeight: 500,
    color: '#3D3A36',
  },
  actionButtons: {
    display: 'flex',
    gap: '8px',
  },
  primaryButton: {
    flex: 1,
    padding: '10px',
    borderRadius: '8px',
    background: '#3D3A36',
    border: 'none',
    color: '#FAF9F7',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
  },
  secondaryButton: {
    flex: 1,
    padding: '10px',
    borderRadius: '8px',
    background: 'white',
    border: '1px solid rgba(93, 86, 78, 0.15)',
    color: '#5C564E',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
};

export default BotsPage;
